import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, NavLink, Route, Routes, useNavigate, useParams } from "react-router-dom";
import type { ResellerBalanceDto, ResellerCustomerDto, ResellerDto, SubscriptionDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { api, ApiFailure } from "../../lib/api";
import { Icon } from "../../components/Icon";
import { Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { errText, fieldErrs, Title } from "../platform/common";
import { CustomerTable, Statement } from "../platform/Resellers";

// The reseller portal under /reseller (migration 048, request of 29 Oct 2026, item 8). A reseller signs in with its
// own account (never a business's or Delta's) and manages the customers Delta assigned to it: their packages and
// dates, and only the actions and packages Delta allows. It sees what it owes Delta, never a customer's books.

interface Pkg { id: string; code: string; nameEn: string; nameAr: string; kind: "trial" | "paid"; trialDays: number | null }
interface Me { user: { id: string; name: string; email: string }; reseller: ResellerDto; packages: Pkg[] }
interface Ctx { me: Me | null; loading: boolean; setMe(m: Me | null): void }
const C = createContext<Ctx>({ me: null, loading: true, setMe: () => undefined });
const useMe = () => useContext(C);
type Sub = Pick<SubscriptionDto, "id" | "plan" | "startsOn" | "endsOn" | "status" | "kind" | "timing" | "originalEndsOn" | "reason">;

export function ResellerApp() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api<Me>("GET", "/reseller/auth/me").then(setMe, () => setMe(null)).finally(() => setLoading(false)); }, []);
  return (
    <C.Provider value={{ me, loading, setMe }}>
      <Routes>
        <Route path="login" element={<Login />} />
        <Route path="setup" element={<Setup />} />
        <Route path="" element={<Frame><Home /></Frame>} />
        <Route path="customers/:id" element={<Frame><Customer /></Frame>} />
        <Route path="statement" element={<Frame><StatementPage /></Frame>} />
        <Route path="*" element={<Navigate to="/reseller" replace />} />
      </Routes>
    </C.Provider>
  );
}

function LangButton() {
  const { t, lang, setLang } = useLang();
  return <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLang(lang === "ar" ? "en" : "ar")}><Icon name="globe" />{t("switchLang")}</button>;
}

function Login() {
  const { t } = useLang();
  const { me, setMe } = useMe();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = `${t("rpSignIn")} · Delta`; }, [t]);
  if (me) return <Navigate to="/reseller" replace />;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try { setMe(await api<Me>("POST", "/reseller/auth/login", { email: email.trim(), password: pw })); nav("/reseller", { replace: true }); }
    catch (x) { setErr(x instanceof ApiFailure && x.code === "too_many_attempts" ? t("tooManyAttempts") : x instanceof ApiFailure && x.code === "reseller_suspended" ? t("rpSuspended") : t("pfBadCredentials")); setPw(""); }
    finally { setBusy(false); }
  };
  return (
    <div className="pf-login">
      <form noValidate onSubmit={submit} data-testid="rp-login">
        <div className="pf-row" style={{ justifyContent: "space-between" }}><b>{t("rpName")}</b><LangButton /></div>
        <h1>{t("rpSignIn")}</h1><p>{t("rpSignInLede")}</p>
        <div className="field"><label htmlFor="rp-e">{t("pfEmail")}</label><input id="rp-e" className="control" dir="ltr" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="field"><label htmlFor="rp-p">{t("password")}</label><input id="rp-p" className="control" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
        {err && <div className="dlg-alert show" role="alert" data-testid="rp-login-error"><Icon name="alert" /><span>{err}</span></div>}
        <button className="btn btn-primary" type="submit" disabled={busy}>{t("pfSignIn")}</button>
        <Link className="pf-small" to="/reseller/setup" data-testid="rp-to-setup">{t("rpFirstTime")}</Link>
      </form>
    </div>
  );
}

/** The first password, with the one-time code Delta gave the person (Delta never chooses or sees the password). */
function Setup() {
  const { t } = useLang();
  const nav = useNavigate();
  const [v, setV] = useState({ email: "", code: "", password: "", again: "" });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setErrs({});
    if (v.password !== v.again) { setErrs({ again: t("rpPasswordsDiffer") }); return; }
    setBusy(true);
    try { await api("POST", "/reseller/auth/setup", { email: v.email.trim(), code: v.code, password: v.password }); nav("/reseller/login", { replace: true, state: { ready: true } }); }
    catch (x) { setErrs(fieldErrs(t, x)); setErr(x instanceof ApiFailure && x.code === "validation" ? t("rpPasswordRule") : errText(t, x)); }
    finally { setBusy(false); }
  };
  return (
    <div className="pf-login">
      <form noValidate onSubmit={submit} data-testid="rp-setup">
        <div className="pf-row" style={{ justifyContent: "space-between" }}><b>{t("rpName")}</b><LangButton /></div>
        <h1>{t("rpSetupTitle")}</h1><p>{t("rpSetupLede")}</p>
        <Field label={t("pfEmail")} htmlFor="rs-se"><input id="rs-se" className="control" dir="ltr" type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>
        <Field label={t("rpCode")} htmlFor="rs-sc"><input id="rs-sc" className="control num" dir="ltr" autoComplete="one-time-code" value={v.code} onChange={(e) => setV({ ...v, code: e.target.value })} /></Field>
        <Field label={t("rpNewPassword")} htmlFor="rs-sp" hint={t("rpPasswordRule")} error={errs.password}><input id="rs-sp" className="control" type="password" autoComplete="new-password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} /></Field>
        <Field label={t("rpPasswordAgain")} htmlFor="rs-sa" error={errs.again}><input id="rs-sa" className="control" type="password" autoComplete="new-password" value={v.again} onChange={(e) => setV({ ...v, again: e.target.value })} /></Field>
        {err && <div className="dlg-alert show" role="alert" data-testid="rp-setup-error"><Icon name="alert" /><span>{err}</span></div>}
        <button className="btn btn-primary" type="submit" disabled={busy}>{t("rpSetPassword")}</button>
        <Link className="pf-small" to="/reseller/login">{t("rpBackToSignIn")}</Link>
      </form>
    </div>
  );
}

function Frame({ children }: { children: ReactNode }) {
  const { t, L } = useLang();
  const { me, loading, setMe } = useMe();
  const nav = useNavigate();
  if (loading) return <div className="state-box"><p>{t("pfLoading")}</p></div>;
  if (!me) return <Navigate to="/reseller/login" replace />;
  return (
    <div className="pf rp">
      <header className="pf-top">
        <span className="brand"><Icon name="users" />{t("rpName")} <small>{L(me.reseller)}</small></span>
        <nav className="pf-nav">
          <NavLink end to="/reseller" className={({ isActive }) => (isActive ? "on" : "")}>{t("rpCustomers")}</NavLink>
          <NavLink to="/reseller/statement" className={({ isActive }) => (isActive ? "on" : "")}>{t("rsStatement")}</NavLink>
        </nav>
        <LangButton />
        <button className="btn btn-sm" data-testid="rp-signout" title={me.user.email} onClick={async () => { await api("POST", "/reseller/auth/logout").catch(() => undefined); setMe(null); nav("/reseller/login"); }}>{t("pfSignOut")}</button>
      </header>
      <main className="pf-main">{children}<p className="pf-boundary"><Icon name="lock" />{t("rpBoundary")}</p></main>
    </div>
  );
}

function Home() {
  const { t, L } = useLang();
  const { me } = useMe();
  const [d, setD] = useState<{ customers: ResellerCustomerDto[]; balance: ResellerBalanceDto; today: string } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => { api<typeof d>("GET", "/reseller/dashboard").then(setD, (e) => setFailed(errText(t, e))); }, [t]);
  if (failed) return <div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div>;
  if (!d || !me) return <div className="state-box"><p>{t("pfLoading")}</p></div>;
  const r = me.reseller;
  const trials = d.customers.filter((c) => c.current?.kind === "trial").length;
  const soon = d.customers.filter((c) => c.daysLeft !== null && c.daysLeft <= 14 && !c.next).length;
  const closed = d.customers.filter((c) => c.status !== "active" || (c.access && c.access !== "open")).length;
  const allowed = (["canStartTrial", "canExtendTrial", "canRenew", "canChangePackage"] as const).filter((k) => r[k]);
  return (
    <>
      <Title text={t("rpHomeTitle", { x: L(r) })} />
      <section className="card"><div className="pf-figs" data-testid="rp-figures">
        <div className="pf-fig info"><b className="num">{d.customers.length}</b><span>{t("rpFigCustomers", { n: r.maxCustomers })}</span></div>
        <div className="pf-fig"><b className="num">{trials}</b><span>{t("rpFigTrials", { n: r.maxActiveTrials })}</span></div>
        <div className="pf-fig warn"><b className="num">{soon}</b><span>{t("rpFigSoon")}</span></div>
        <div className="pf-fig bad"><b className="num">{closed}</b><span>{t("rpFigClosed")}</span></div>
        <Link className="pf-fig" to="/reseller/statement" data-testid="rp-owed"><b className="num">{`${d.balance.outstanding} ${d.balance.currency ?? ""}`.trim()}</b><span>{t("rpFigOwed")}</span></Link>
      </div>
        <p className="pf-small" style={{ padding: "0 20px 16px", margin: 0 }} data-testid="rp-allowed">{allowed.length ? t("rpAllowed", { x: allowed.map((k) => t(`rsPerm_${k}`)).join(", ") }) : t("rpAllowedNone")}</p></section>
      <section className="card list-card"><div className="card-head"><h2>{t("rpCustomers")}</h2></div>
        <CustomerTable customers={d.customers} link={(c) => `/reseller/customers/${c.id}`} testId="rp-customers" /></section>
    </>
  );
}

type Act = "trial" | "extend" | "renew" | "change";

function Customer() {
  const { t, L } = useLang();
  const { me } = useMe();
  const { id } = useParams();
  const [d, setD] = useState<{ customer: ResellerCustomerDto; subscriptions: Sub[] } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [open, setOpen] = useState<Act | null>(null);
  const load = useCallback(() => api<typeof d>("GET", `/reseller/customers/${id}`).then(setD, (e) => setFailed(errText(t, e))), [id, t]);
  useEffect(() => { void load(); }, [load]);
  if (failed) return <div className="state-box error" data-testid="rp-customer-error"><Icon name="alert" /><h3>{failed}</h3></div>;
  if (!d || !me) return <div className="state-box"><p>{t("pfLoading")}</p></div>;
  const c = d.customer, r = me.reseller;
  const trialNow = c.current?.kind === "trial";
  const acts: [Act, boolean][] = [["trial", r.canStartTrial && !c.trialUsed && c.status === "active"], ["extend", r.canExtendTrial && trialNow], ["renew", r.canRenew && d.subscriptions.some((s) => s.status === "active")], ["change", r.canChangePackage]];
  return (
    <>
      <Link className="pf-small" to="/reseller">{t("rpCustomers")}</Link>
      <section className="card pf-summary" data-testid="rp-summary">
        <div className="pf-sum-head"><div><Title text={L(c)} />{c.ownerName && <div className="pf-small">{t("bdOwner")}: <b>{c.ownerName}</b> · <span dir="ltr">{c.ownerMobile}</span> · {t("rpSince", { d: c.assignedOn })}</div>}</div></div>
        <dl className="pf-sum-grid">
          <div><dt>{t("bdAccess")}</dt><dd>{c.status !== "active" ? <span className="pill bad">{t(`bdClosedStatus_${c.status}`)}</span> : c.access && c.access !== "open" ? <span className="pill bad">{t(`pfClosed_${c.access}`)}</span> : <span className="pill good">{t("bdOpen")}</span>}</dd></div>
          <div><dt>{t("bdPackage")}</dt><dd>{c.current ? <><b>{L(c.current.plan)}</b><span className={`pill ${trialNow ? "info" : "good"}`} data-testid="rp-kind">{t(`pkgKind_${c.current.kind}`)}</span></> : <span className="pill">{t("pfPkg_none")}</span>}</dd></div>
          <div><dt>{t("bdExpiry")}</dt><dd className="num" data-testid="rp-expiry">{c.current?.endsOn ?? "--"}{c.next && <span className="pf-small">{t("bdThen", { x: L(c.next.plan), d: c.next.startsOn })}</span>}</dd></div>
          <div><dt>{t("bdDaysLeft")}</dt><dd className="num" data-testid="rp-days-left">{c.daysLeft === null ? "--" : c.daysLeft === 1 ? t("bdLastDay") : c.daysLeft}</dd></div>
          <div><dt>{t("rsBilling")}</dt><dd>{t(`rsBilling_${c.billing}`)}{c.rate && <span className="pf-small num">{c.rate.amount} {c.rate.currency}</span>}</dd></div>
        </dl>
      </section>
      <div className="pf-row pf-head-actions" data-testid="rp-actions">
        {acts.filter(([, on]) => on).map(([k]) => <button key={k} className={`btn ${k === "change" ? "btn-primary" : ""}`} data-testid={`rp-act-${k}`} onClick={() => setOpen(k)}>{t(`rpAct_${k}`)}</button>)}
        {!acts.some(([, on]) => on) && <span className="pf-small">{t("rpNoActions")}</span>}
      </div>
      <section className="card list-card"><div className="card-head"><h2>{t("pfSubscriptions")}</h2></div>
        {!d.subscriptions.length ? <div className="state-box"><p>{t("bdNoSubs")}</p></div> : (
          <div className="pf-scroll"><table className="tbl" data-testid="rp-subs"><thead><tr><th>{t("pkgName")}</th><th>{t("pkgDates")}</th><th>{t("pfTimingCol")}</th></tr></thead>
            <tbody>{d.subscriptions.map((s) => (
              <tr key={s.id} data-kind={s.kind}><td>{L(s.plan)} <span className="pf-small">{t(`pkgKind_${s.kind}`)}</span><div className="pf-small">{s.reason}</div></td>
                <td className="num">{s.startsOn} → {s.endsOn}{s.originalEndsOn && <div className="pf-small">{t("pkgExtendedFrom", { d: s.originalEndsOn })}</div>}</td>
                <td><span className={`pill ${s.timing === "current" ? "good" : s.timing === "scheduled" ? "info" : ""}`}>{t(`pfTiming_${s.timing}`)}</span></td></tr>))}</tbody></table></div>)}
      </section>
      {open && <ActDialog act={open} c={c} me={me} onClose={() => setOpen(null)} onDone={(x) => { setD(x); setOpen(null); }} />}
    </>
  );
}

function ActDialog({ act, c, me, onClose, onDone }: { act: Act; c: ResellerCustomerDto; me: Me; onClose(): void; onDone(d: { customer: ResellerCustomerDto; subscriptions: Sub[] }): void }) {
  const { t, L } = useLang();
  const toast = useToast();
  const r = me.reseller;
  const pkgs = me.packages.filter((p) => p.kind === (act === "trial" ? "trial" : "paid"));
  const today = new Date(Date.now() + 3 * 3_600_000).toISOString().slice(0, 10);
  const plus = (d: string, n: number) => new Date(Date.parse(d) + n * 86_400_000).toISOString().slice(0, 10);
  const [v, setV] = useState({ planId: act === "renew" ? "" : pkgs[0]?.id ?? "", startsOn: today, effectiveOn: today, endsOn: act === "extend" && c.current ? plus(c.current.endsOn, 1) : "", reason: "" });
  const [requestId] = useState(() => crypto.randomUUID());
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const body = act === "trial" ? { planId: v.planId, startsOn: v.startsOn, reason: v.reason, requestId } : act === "extend" ? { endsOn: v.endsOn, reason: v.reason }
    : act === "renew" ? { ...(v.planId ? { planId: v.planId } : {}), endsOn: v.endsOn, reason: v.reason, requestId } : { planId: v.planId, effectiveOn: v.effectiveOn, endsOn: v.endsOn, reason: v.reason, requestId };
  const maxEnd = act === "extend" && c.current ? plus(c.current.endsOn, r.maxTrialExtensionDays) : undefined;
  return (
    <Modal title={t(`rpAct_${act}`)} sub={L(c)} onClose={onClose} busy={busy} alert={alert} saveLabel={t(`rpAct_${act}`)} testId="rp-act-dialog" saveTestId="rp-act-submit"
      saveDisabled={v.reason.trim().length < 5 || (act !== "trial" && !v.endsOn) || ((act === "trial" || act === "change") && !v.planId)}
      onSubmit={async () => {
        setBusy(true);
        try { onDone(await api("POST", `/reseller/customers/${c.id}/${act}`, body)); toast(t("pfSaved")); } catch (e) { setErrs(fieldErrs(t, e)); setAlert(errText(t, e)); } finally { setBusy(false); }
      }}>
      <div className="pf-dialog">
        <p className="pf-small">{t(`rpActHint_${act}`, { n: r.maxTrialExtensionDays })}</p>
        {act !== "extend" && <Field label={t("bdPackage")} req={act !== "renew"} error={errs.planId}>
          <select className="control" data-testid="rp-plan" value={v.planId} onChange={(e) => setV({ ...v, planId: e.target.value })}>
            {act === "renew" && <option value="">{t("pfRenewSame")}</option>}
            {pkgs.map((p) => <option key={p.id} value={p.id}>{L(p)}{p.kind === "trial" && p.trialDays ? ` · ${t("pkgTrialDays", { n: p.trialDays })}` : ""}</option>)}</select></Field>}
        <div className="pf-dates">
          {act === "trial" && <Field label={t("pfStarts")} req error={errs.startsOn}><input className="control num" type="date" min={today} value={v.startsOn} onChange={(e) => setV({ ...v, startsOn: e.target.value })} /></Field>}
          {act === "change" && <Field label={t("pfEffective")} req error={errs.effectiveOn}><input className="control num" type="date" min={today} value={v.effectiveOn} onChange={(e) => setV({ ...v, effectiveOn: e.target.value })} /></Field>}
          {act !== "trial" && <Field label={t(act === "extend" ? "pfNewEnd" : "pfEnds")} req error={errs.endsOn}><input className="control num" type="date" max={maxEnd} data-testid="rp-ends" value={v.endsOn} onChange={(e) => setV({ ...v, endsOn: e.target.value })} /></Field>}
        </div>
        <Field label={t("pfReason")} req hint={t("bdReasonHint")}><textarea className="control" rows={2} value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}

function StatementPage() {
  const { t } = useLang();
  const today = new Date(Date.now() + 3 * 3_600_000).toISOString().slice(0, 10);
  return <><Title text={t("rsStatement")} /><p className="pf-small no-print">{t("rpStatementLede")}</p><Statement base="/reseller" today={today} /></>;
}
